import '@awesome.me/webawesome/dist/components/button/button.js';

import type { SurfacedItem } from '@app/core';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { HeatBar } from './HeatBar.js';

interface FocusCardProps {
  surfaced: SurfacedItem;
  onComplete: () => void;
  onSnooze: () => void;
  onDismiss: () => void;
  onReheat: () => void;
}

export function FocusCard({ surfaced, onComplete, onSnooze, onDismiss, onReheat }: FocusCardProps) {
  const { t } = useTranslation();
  const { item, temperature, fading } = surfaced;
  const cardRef = useRef<HTMLDivElement>(null);

  // Move focus to the card when the focused item changes
  useEffect(() => {
    cardRef.current?.focus();
  }, [item.id]);

  return (
    <div
      ref={cardRef}
      tabIndex={-1}
      role="region"
      aria-live="polite"
      aria-label={item.text}
      style={{
        background: 'var(--color-card)',
        border: '1px solid var(--color-card-border)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-xl)',
        maxWidth: '480px',
        width: '100%',
        margin: '0 auto',
      }}
    >
      <div style={{ marginBottom: 'var(--space-md)' }}>
        {item.effort && (
          <span
            style={{
              fontSize: '0.75rem',
              color: 'var(--color-text-secondary)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            {t(`items:effort.${item.effort}`)}
          </span>
        )}
      </div>

      <h2
        style={{
          fontFamily: 'var(--font-display)',
          fontSize: '1.5rem',
          marginBottom: 'var(--space-sm)',
        }}
      >
        {item.text}
      </h2>

      {item.consequence && (
        <p
          style={{
            color: 'var(--color-text-secondary)',
            fontSize: '0.875rem',
            marginBottom: 'var(--space-md)',
          }}
        >
          {item.consequence}
        </p>
      )}

      <HeatBar temperature={temperature} />

      <div
        style={{
          display: 'flex',
          gap: 'var(--space-sm)',
          marginTop: 'var(--space-lg)',
          flexWrap: 'wrap',
        }}
      >
        {fading ? (
          <>
            <wa-button
              variant="brand"
              aria-label={t('actions:stillMattersLabel', { item: item.text })}
              onClick={onReheat}
            >
              {t('actions:stillMatters')}
            </wa-button>
            <wa-button
              variant="neutral"
              appearance="outlined"
              aria-label={t('actions:letItGoLabel', { item: item.text })}
              onClick={onDismiss}
            >
              {t('actions:letItGo')}
            </wa-button>
          </>
        ) : (
          <>
            <wa-button
              variant="brand"
              aria-label={t('actions:doneLabel', { item: item.text })}
              onClick={onComplete}
            >
              {t('actions:done')}
            </wa-button>
            <wa-button variant="neutral" appearance="outlined" onClick={onSnooze}>
              {t('actions:notNow')}
            </wa-button>
            <wa-button variant="neutral" appearance="outlined" onClick={onDismiss}>
              {t('actions:dropIt')}
            </wa-button>
          </>
        )}
      </div>

      {fading && (
        <p
          style={{
            color: 'var(--color-text-muted)',
            fontSize: '0.8rem',
            marginTop: 'var(--space-md)',
          }}
        >
          {t('items:fadingPrompt')}
        </p>
      )}
    </div>
  );
}
